import { FC, ReactElement } from 'react';

import { ErrorLabel, Icon, ProductPrice } from '../../../../components';
import { Accordion, Button, TooltipWrapper } from '../../../../compositions';
import { maximumOrderTotal } from '../../../../constants';
import { CouponCodeFormData } from '../../../../entities/@forms/CouponCodeForm/CouponCodeForm';
import { PriceBaseInterface } from '../../../../entities/Price/Price';
import { useTrans } from '../../../../hooks';
import { CouponCodeForm } from '../../../@forms/CouponForm/CouponCodeForm';

import './Pricing.scss';

interface PricingProps extends PriceBaseInterface {
    isLoading?: boolean;
    hideCouponCodeInput?: boolean
    onSetCouponCode?: (data: CouponCodeFormData) => void;
    onRemoveCouponCode?: () => void;
    className?: string;
    totalPriceClassName?: string;
}

export const Pricing: FC<PricingProps> = ({
    isLoading,
    hideCouponCodeInput,
    subTotalPrice,
    handlingPrice,
    totalPrice,
    discounts,
    onSetCouponCode,
    onRemoveCouponCode,
    className = '',
    totalPriceClassName = '',
}): ReactElement => {
    const trans = useTrans();

    const totalExceedsMaximum = totalPrice >= maximumOrderTotal;

    const handleCouponCodeSubmit = (data: CouponCodeFormData): void => {
        onSetCouponCode?.(data);
    };

    return (
        <div className={`pricing ${className}`}>
            <div className="pricing__price-item">
                <p>
                    {trans('containers.checkout.pricing.subTotal')}
                </p>

                <ProductPrice
                    isCents
                    includeCurrencySymbol
                    isLoading={isLoading}
                    current={subTotalPrice}
                    original={subTotalPrice}
                    className="pricing__price"
                />
            </div>

            {!isLoading && discounts.map(discount => (
                <div key={discount.id} className="pricing__price-item">
                    <p>
                        {discount.label && (
                            <span className="pricing__discount-label">
                                {discount.label}
                            </span>
                        )}
                    </p>

                    {discount.invalid && (
                        <TooltipWrapper
                            id="incorrect-discountCode-tooltip"
                            text={trans('containers.checkout.pricing.incorrectDiscountCode')}
                            className="pricing__tooltip-wrapper"
                            tooltipClassName="pricing__tooltip"
                        >
                            <Icon name="box-warning" className="pricing__tooltip-icon" />
                        </TooltipWrapper>
                    )}

                    <ProductPrice
                        isCents
                        includeCurrencySymbol
                        current={discount.value}
                        original={discount.value}
                        className="pricing__price pricing__price--is-sale"
                    />

                    {!hideCouponCodeInput && !discount.productDiscount && (
                        <Button
                            text={trans('common.delete')}
                            onClick={onRemoveCouponCode}
                            className="pricing__remove-discount-button"
                        />
                    )}
                </div>
            ))}

            {!hideCouponCodeInput && (
                <Accordion
                    heading={trans('containers.checkout.pricing.discountCode')}
                    className="pricing__accordion"
                    headingClassName="pricing__accordion-heading"
                    contentClassName="pricing__accordion-content"
                >
                    <CouponCodeForm
                        onSubmit={handleCouponCodeSubmit}
                        className="pricing__coupon-form"
                    />
                </Accordion>
            )}

            <div className="pricing__price-item">
                <p>
                    {trans('containers.checkout.pricing.shippingCosts')}
                </p>

                <ProductPrice
                    isCents
                    includeCurrencySymbol
                    isLoading={isLoading}
                    current={handlingPrice}
                    original={handlingPrice}
                    className="pricing__price"
                />
            </div>

            <div className={`pricing__price-item pricing__price-total ${totalPriceClassName}`}>
                <p>
                    {trans('containers.checkout.pricing.total')}
                </p>

                <ProductPrice
                    isCents
                    includeCurrencySymbol
                    isLoading={isLoading}
                    current={totalPrice}
                    original={totalPrice}
                />
            </div>

            {totalExceedsMaximum && (
                <ErrorLabel text={trans('containers.checkout.pricing.totalExceedsMaximum')} />
            )}
        </div>
    );
};
